"use client";
import { createContext, useContext, useState, useRef, useEffect } from "react";
import LogoLoader from "@/components/common/LogoLoader";

// Create a context for the loader state
const LoaderContext = createContext(undefined);

// Custom hook to use the loader context
export function useLoader() {
  const context = useContext(LoaderContext);
  if (context === undefined) {
    throw new Error("useLoader must be used within a LoaderProvider");
  }
  return context;
}

// Provider component that wraps your app and makes loader state available
export function LoaderProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [fadingOut, setFadingOut] = useState(false);
  const timeoutRef = useRef(null);

  // Function to show loader with optional message
  const showLoader = (message = "") => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setFadingOut(false);
    setLoadingMessage(message);
    setLoading(true);
  };

  // Function to hide loader with smooth transition
  const hideLoader = () => {
    // Start the fade out animation
    setFadingOut(true);
    
    // Set a timeout to actually remove the loader from DOM after animation completes
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      setLoadingMessage("");
      setFadingOut(false);
    }, 300); // This should match the CSS transition duration
  };

  // Clean up any timeouts when the component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Value object that will be passed to consumers
  const value = {
    loading,
    loadingMessage,
    showLoader,
    hideLoader,
  };

  return (
    <LoaderContext.Provider value={value}>
      {children}
      {loading && (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            backgroundColor: "rgba(19, 47, 67, 1)",
            zIndex: 9999,
            opacity: fadingOut ? 0 : 1,
            transition: "opacity 300ms ease-out",
          }}
        >
          <div className="text-center">
            <LogoLoader logoSrc="/images/loader.png" altText="Warrantix Logo" />
          </div>
        </div>
      )}
    </LoaderContext.Provider>
  );
}